import { AfterViewInit, ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule } from '@jsverse/transloco';
import { ExpandService } from 'libs/shared/util-common/src/lib/expand.service';
import { GuidelineService, GuidelineViewService, MergeConflictService, ScrollService } from 'shared/domain';
import { IconDirective } from 'shared/ui-common';
import { isConnectedGuideline } from 'shared/util-common';

import { CommentableElementDirective } from '@treetop/shared/comments';
import { AttachmentsLinksReferencesTag } from '@treetop/shared/guideline-tags';
import {
  ComponentExpandStrategy,
  ComponentWidget,
  GuidelineActionSignificance,
  GuidelineComponentType,
  GuidelineTag,
  GuidelineTagStatus,
  TextWidget,
  UpdateStatus,
} from '@treetop/shared/model';
import { ResourceTagListComponent } from '@treetop/shared/tt-resource-tags';

import { GuidelineUpdateTagComponent } from '../../../../../../guideline-edit/edit-widgets/edit-chapter-widget/guideline-update-tag/guideline-update-tag.component';
import { TextWidgetComponent } from '../../text/text-widget.component';

@Component({
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'tt-component-widget',
  templateUrl: './component-widget.component.html',
  styleUrls: ['./component-widget.component.scss'],
  imports: [
    AttachmentsLinksReferencesTag,
    IconDirective,
    MatIconModule,
    TranslocoModule,
    GuidelineUpdateTagComponent,
    CommentableElementDirective,
    TextWidgetComponent,
    ResourceTagListComponent,
  ],
  providers: [MergeConflictService],
  host: {
    '[attr.data-uniqueid]': 'widget().uniqueId',
  },
})
export class ComponentWidgetComponent implements AfterViewInit {
  private expandService = inject(ExpandService);
  private guidelineService = inject(GuidelineService);
  protected guidelineViewService = inject(GuidelineViewService);
  private scrollService = inject(ScrollService);
  private elementRef = inject(ElementRef);

  public parentComponentId = input<string | null>(null);
  public widget = input.required<ComponentWidget>();
  public allowEditTags = input<boolean>(false);
  public expandStrategy = input<ComponentExpandStrategy>(ComponentExpandStrategy.Default);

  protected component = computed(() => this.widget().component);
  protected descriptionWidget = computed(() => {
    const widgetIds = this.widget().widgetIds;
    if (widgetIds?.length === 0) {
      return undefined;
    }

    return this.guidelineService.getWidget<TextWidget>(widgetIds[0]);
  });
  protected childrenWidgets = computed(() => {
    return this.widget().childrenIds?.map((childId) => this.guidelineService.getWidget<ComponentWidget>(childId)) ?? [];
  });

  protected hasDescription = computed(() => {
    const descriptionWidget = this.descriptionWidget();

    if (descriptionWidget?.text) {
      return true;
    }

    return descriptionWidget?.localChanges?.text && descriptionWidget.localChanges.text !== '';
  });

  protected hasContent = computed(() => {
    if (this.hasDescription()) {
      return true;
    }

    return this.childrenWidgets().length > 0;
  });

  protected expanded = computed(() => this.expandService.getExpandState(this.widget().uniqueId)());
  protected guidelineData = this.guidelineService.guidelineData;

  protected optional = computed(() => {
    const component = this.component();
    if (component.componentType === GuidelineComponentType.Action) {
      return component.significance === GuidelineActionSignificance.Optional;
    }
    return false;
  });

  protected medicationRequests = computed(() => {
    const component = this.component();
    if (component.componentType === GuidelineComponentType.Action) {
      return component.medicationRequests;
    }
    return [];
  });

  protected title = computed(() => {
    const component = this.component();
    if (component.title) {
      return component.title;
    } else if (component.componentType === GuidelineComponentType.Action) {
      return component.activityDefinition?.title;
    }
    return '';
  });

  protected originalTag = computed(() => {
    if (!this.widget()) {
      return undefined;
    }

    const originalTag = this.widget().tag;

    if (!originalTag) {
      return undefined;
    }

    if (isConnectedGuideline(this.guidelineData().guidelineOrigin)) {
      if (this.guidelineData().updateStatus === UpdateStatus.DEFAULT) {
        return undefined;
      }

      if (
        !this.guidelineViewService?.isSmartbookMode() &&
        !this.guidelineData().environment.hasPublishedVersion &&
        originalTag.status !== GuidelineTagStatus.NEW
      ) {
        return undefined;
      }
    }

    return originalTag;
  });

  protected localTag = computed(() => {
    const originalTag = this.widget().tag;
    const localTag = this.widget().localChanges?.tag;

    if (!localTag) {
      return undefined;
    }

    if (originalTag?.status === GuidelineTagStatus.DELETED) {
      return undefined;
    }

    if (!this.guidelineViewService?.isSmartbookMode() && !this.guidelineData().environment.hasPublishedVersion) {
      return undefined;
    }

    return localTag;
  });

  protected tag = computed(() => {
    if (!this.guidelineViewService?.showTags()) {
      return undefined;
    }

    return this.localTag() ?? this.originalTag();
  });

  protected attachments = computed(() => {
    const widget = this.widget();
    if (!widget) {
      return [];
    }

    const chapterAttachments = (widget.attachments ?? []).filter(
      (attachment) => widget.localChanges?.deletedOriginalAttachmentIds?.includes(attachment.id) !== true,
    );

    const localAttachments = this.widget()?.localChanges?.attachments ?? [];

    return [...chapterAttachments, ...localAttachments];
  });

  constructor() {
    effect(() => {
      const widget = this.widget();

      if (!widget) {
        return;
      }

      this.expandService.addItem(widget.uniqueId, this.expandStrategy() === ComponentExpandStrategy.Expanded, this.parentComponentId());
    });
  }

  protected toggleExpanded() {
    this.expandService.setExpandState(this.widget().uniqueId!, !this.expanded());
  }

  protected handleTagUpdated(tag: GuidelineTag) {
    this.guidelineService.updateWidgetTag(this.widget().uniqueId, tag);
  }

  public ngAfterViewInit(): void {
    this.scrollService.register(this.elementRef);
  }

  protected readonly GuidelineActionSignificance = GuidelineActionSignificance;
}
